

import { HighLightableInterface } from './high.lightable.interface';
import { HighLightInterface }     from './high.light.interface';
import { Logger }                 from '@/util/logger';
import { HighLighterInterface }   from './high.lighter.interface';
import { TrackLabel }             from './track.label';
import { Connection }             from './connection';
import { DecisionPoint }          from '@/models/scenario/decision.point';
import { Track }                  from '@/models/scenario/track';

import { v4 as uuidv4 }           from 'uuid';


/**
 *
 * @class HighLightManager
 *
 * when a highlighting of one or more canvas objects is required, it gets
 * tracked and managed here.  Some highlighting actions lead to highlighting
 * other objects, rather than just the object being clicked or hovered on.
 * So the highlighting manager has global knowledge of all objects in the
 * virtual canvas and can take appropriate action to add or remove/clear
 * highlights.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class HighLightManager implements HighLighterInterface {

  /** the currently selected decision point */
  public dp: DecisionPoint;

  /** the tracks to east of the current decision point */
  private east: HighLightableInterface[]           = [];

  /** the tracks to the west of current decision point */
  private west: HighLightableInterface[]           = [];

  /** the junctions */
  private junctions: HighLightableInterface[]      = [];

  /** the connection web between tracks */
  private connections: HighLightableInterface[]    = [];

  /** the objects that currently have a highlight */
  private highLighted: HighLightableInterface[]    = [];

  /** the thing we are hovering over right now */
  private over: HighLightableInterface|null        = null;

  /** the thing we last clicked on */
  private selected: HighLightableInterface|null    = null;

  /** commonly used styling for connections */
  private templates: Map<string, HighLightInterface> = new Map<string, HighLightInterface>();

  /* by default don't color connections by speed */
  private colorSpeed: boolean                     = false;

  /** standard constructor, colorMode == true, for coloring tracks by speed. */
  constructor(
    dp:        DecisionPoint,
    east:      HighLightableInterface[],
    west:      HighLightableInterface[],
    junctions: HighLightableInterface[],
    routes:    HighLightableInterface[],
    colorMode: boolean) {


    this.dp         = dp;
    this.colorSpeed = colorMode;

    /* set up the commonly used styles */

    this.templates.set('blue', {
      id:    uuidv4(),
      color: '#95c5f8',
      opacity: 1.0
    });

    this.templates.set('red', {
      id:    uuidv4(),
      color: '#ffb0b0',
      opacity: 1.0
    });

    this.templates.set('green', {
      id:    uuidv4(),
      color: '#61df59',
      opacity: 1.0
    });

    this.templates.set('free', {
      id:    uuidv4(),
      color: '#61df59',
      opacity: 1.0
    });

    this.templates.set('faded', {
      id:    uuidv4(),
      color: 'white',
      opacity: 0.18
    });

    this.templates.set('bright-gray', {
      id:    uuidv4(),
      color: '#cdcdcd',
      opacity: 1.0
    });

    this.templates.set('white', {
      id:    uuidv4(),
      color: 'white',
      opacity: 1.0
    });

    this.templates.set('default-connector-color', {
      id:    uuidv4(),
      color: 'white',
      opacity: 0.18
    });

    this.templates.set('speed-15', {
      id:    uuidv4(),
      color: '#f9796c',
      opacity: 0.18
    });

    this.templates.set('speed-30', {
      id:    uuidv4(),
      color: '#ecef76',
      opacity: 0.18
    });

    this.templates.set('speed-45', {
      id:    uuidv4(),
      color: '#78ef76',
      opacity: 0.18
    });

    this.templates.set('speed-46', {
      id:    uuidv4(),
      color: '#e576ef',
      opacity: 0.18
    });

    /* establish all the things we track, and ensure styling is consistent */

    this.set(east, west, junctions, routes);

  }

  /** helper for setting the tracks and routes */
  public set(east:      HighLightableInterface[],
             west:      HighLightableInterface[],
             junctions: HighLightableInterface[],
             routes:    HighLightableInterface[]): HighLightManager {

    Logger.debug(`HighLightManager set() - east=${this.east.length}, west=${this.west.length}, junctions=${this.junctions.length}, routes=${this.connections.length}`);

    this.east        = east;
    this.west        = west;
    this.junctions   = junctions;
    this.connections = routes;

    /* ensure styling is what we expect it to be without any user interaction */

    if (!this.colorSpeed) {

      this.defaultHighlighting();

    } else {

      this.speedHighlighting();
    }

    return this;
  }

  /** remove the highlight for the given object. */
  public unHighLight(obj: HighLightableInterface): HighLightManager {

    this.highLight(obj, null);

    return this;
  }

  /** apply a highlight to a canvas objects, if null clear the highlight, and remove from highlighted set */
  public highLight(obj: HighLightableInterface, style: HighLightInterface|null): HighLightManager {

    if (!obj) {

      /* nothing to do */

      return this;
    }

    /* remove this object from the highlighted set (if present)  */

    this.highLighted = this.highLighted.filter((x: HighLightableInterface): boolean => {

      if (!x.highLightStyle || !obj.highLightStyle) {

        return true;
      }

      return x.highLightStyle.id != obj.highLightStyle.id;
    });

    /* are we removing the highlight? */

    if (!style) {

      /* remove highlighting from object itself */

      obj.highLight(null);

      /* we're un-highlighting so we're done! */

      return this;
    }

    /* apply the style and add to the highlighted set */

    obj.highLight(style);

    this.highLighted.push(obj);

    return this;
  }

  /** remove / clear any highlight from all highlighted objects */
  public clear(): HighLightManager {

    let hm: HighLightManager = this;

    Logger.debug(`HighLightManager clearing!`);

    /* remove highlights from each highlighted item */

    hm.highLighted.forEach((item: HighLightableInterface): void => {

      item.highLight(null);
    });

    /* clear the highlighted list */

    hm.highLighted = [];

    hm.over        = null;
    hm.selected    = null;

    return hm;
  }

  private doHighlight(obj: HighLightableInterface): void {

    let hm: HighLightManager = this;

    /* (re) highlight */

    this.clear();

    if (obj instanceof TrackLabel) {

      hm.highlightTrack(obj as TrackLabel);

    } else  if (obj instanceof Connection) {

      hm.highlightConnection(obj as Connection);
    }

  }

  /** apply any hover style override (ignore if object is clicked) */
  public hovering(obj: HighLightableInterface): void {

    let hm: HighLightManager = this;

    /* if we are hovering on something we've already hovered, we can ignore. */

    if (hm.over && obj == hm.over) {

      /* we're already hovering over it */

      return ;
    }

    /* (re) highlight */

    this.doHighlight(obj);

    /* remember what we are hovering over now */

    hm.over = obj;
  }

  /** remove any hover style override (ignore if object is clicked) */
  public leaving(obj: HighLightableInterface): void {

    let hm: HighLightManager = this;

    /* we're done hovering */

    hm.over = null;

    /*
     * if they clicked while they were over the object, then the selection
     * is persistent...at least until they hover or click again somewhere
     * (which might be this object again).
     *
     */

    if (hm.selected) {

      /* don't clear, leave highlight in place until they click or hover again */

      return ;
    }

    /* clear any existing styling anywhere */

    hm.clear();

  }

  /** toggle any click/select style override, clears any existing clicked object */
  public clicked(obj: HighLightableInterface): void {

    let hm: HighLightManager = this;

    /*
     * if are clicking on the same thing that we already clicked...it means toggle,
     * and since its already on...we now clear.
     *
     */

    if (hm.selected == obj) {

      hm.clear();

      return ;
    }

    /* (re) highlight */

    hm.doHighlight(obj);

    hm.selected = obj;

  }

  /** convenience to find the TrackLabel for a given Track */
  public labelForTrack(id: number|Track|TrackLabel|null|undefined): TrackLabel|null {

    let hm: HighLightManager  = this;
    let found: TrackLabel|null;

    if (!id) {

      /* nothing to search for */

      return null;
    }

    if (id instanceof Track) {

      id = id._id;
    }

    if (id instanceof TrackLabel) {

      if (!id.track) {

        /* no track! */

        return null;
      }

      id = id.track._id;
    }

    /* try east block */

    found = hm.east.find((x: HighLightableInterface): boolean => {

      let item: TrackLabel = x as TrackLabel;

      if (!item.track) {

        /* label is missing its track */

        return false;
      }

      return item.track._id == id;

    }) as TrackLabel ?? null;

    /* try west block */

    if (!found) {

      found = hm.west.find((x: HighLightableInterface): boolean => {

        let item: TrackLabel = x as TrackLabel;

        if (!item.track) {

          /* label is missing its track */

          return false;
        }

        return item.track._id == id;

      }) as TrackLabel ?? null;
    }

    /* try junctions */

    if (!found) {

      found = hm.junctions.find((x: HighLightableInterface): boolean => {

        let item: TrackLabel = x as TrackLabel;

        if (!item.track) {

          /* label is missing its track */

          return false;
        }

        return item.track._id == id;

      }) as TrackLabel ?? null;
    }

    /* pass back whatever we found */

    return found;
  }

  /** apply global highlighting for this track being selected */
  public highlightTrack(trackLabel: TrackLabel): void {

    let hm: HighLightManager = this;

    Logger.debug(`HighLightManager highlighting track: `, trackLabel);

    if (!trackLabel.track) {

      /* bad label, they must all have a track */

      return ;
    }

    let me: Track = trackLabel.track;

    /*
     * highlight rules:
     *
     * - this link gets highlighted in blue
     * - reachable links get highlighted in green
     * - connections in reach are left alone
     * - connections not in reach are given low opacity (faded out)
     *
     */

    /* this track */

    let blueStyle: HighLightInterface|null = this.templates.get('blue') ?? null;

    if (blueStyle) {

      hm.highLight(trackLabel, blueStyle);
    }


    /* find the links we can reach... */

    let destinations: TrackLabel[] = [];
    let faded: Connection[]        = [];
    let paths: Connection[]        = [];

    hm.connections.forEach( (c: HighLightableInterface): void => {

      /* work on next possible connection */

      let con: Connection = c as Connection;

      if (!con.src || !con.dst) {

        /* bad connection, they all have to have a from/to */

        faded.push(con);

        return ;
      }

      if (con.src._id == me._id) {

        /* gather other side */

        let other: TrackLabel|null = hm.labelForTrack(con.dst);

        if (other) {

          destinations.push(other);
        }

        paths.push(con);

        return ;
      }

      if (con.dst._id == me._id) {

        /* gather other side */

        let other: TrackLabel|null = hm.labelForTrack(con.src);

        if (other) {

          destinations.push(other);
        }

        paths.push(con);

        return ;
      }

      faded.push(con);

    });

    /* the reachable links all get green highlighting */

    let greenStyle: HighLightInterface|null = this.templates.get('green') ?? null;

    if (greenStyle) {

      destinations.forEach((x: TrackLabel): void => {

        hm.highLight(x, greenStyle);

      });
    }

    /* make the paths a little brighter */

    let brighterStyle: HighLightInterface|null = this.templates.get('bright-gray') ?? null;

    if (brighterStyle) {

      paths.forEach((x: Connection): void => {

        hm.highLight(x, brighterStyle);

      });
    }

    /* fade out the unrelated connections */

    let fadedStyle: HighLightInterface|null = this.templates.get('faded') ?? null;

    faded.forEach((x: Connection): void => {

        if (!fadedStyle) {

          return ;
        }

        if (hm.colorSpeed) {

          /* if we are in speed coloring mode, we want faded, but also the speed color */

          fadedStyle.color = hm.colorForSpeed(x.speed);
        }

        hm.highLight(x, fadedStyle);

    });

  }

  /** convenience to look up style for speed */
  public styleForSpeed(speed: number): HighLightInterface {

    /* now select the speed coloring ... */

    let speedStyle: HighLightInterface|null = null;

    if (speed >= 1 && speed <= 15) {

      speedStyle = this.templates.get('speed-15') ?? null;

    } else if (speed >= 16 && speed <= 30) {

      speedStyle = this.templates.get('speed-30') ?? null;

    } else if (speed >= 16 && speed <= 30) {

      speedStyle = this.templates.get('speed-30') ?? null;

    } else if (speed >= 31 && speed <= 45) {

      speedStyle = this.templates.get('speed-45') ?? null;

    } else if (speed >= 46) {

      speedStyle = this.templates.get('speed-46') ?? null;
    }

    if (!speedStyle) {

      return {
        id:     uuidv4(),
        color:  'white',
        opacity: 0.18
      };

    }

    return speedStyle;
  }

  /** convenience to find color for speed (HTML color spec) */
  public colorForSpeed(speed: number): string {

    let style: HighLightInterface = this.styleForSpeed(speed);

    return style?.color ?? 'white';
  }

  /** force the connections / routes to be colored by speed, unless you are hovering / clicking them. */
  public speedHighlighting(): void {

    this.colorSpeed = true;

    this.connections.forEach((x: HighLightableInterface):void => {

      /* fetch the default styling */

      let connection: Connection         = x as Connection;

      connection.highLight(this.styleForSpeed(connection.speed));

    });

  }

  /** force reset the colors back to the defaults */
  public defaultHighlighting(): void {

    this.colorSpeed = false;

    /* east tracks */

    this.east.forEach((x: HighLightableInterface): void => {

      let basic: HighLightInterface|null = this.templates.get('white') ?? null;

      if (!basic) {

        /* missing template? */

        return ;
      }

      x.highLight(basic);
    });

    /* west tracks */

    this.west.forEach((x: HighLightableInterface): void => {

      let basic: HighLightInterface|null = this.templates.get('white') ?? null;

      if (!basic) {

        /* missing template? */

        return ;
      }

      x.highLight(basic);
    });

    /* junctions */

    this.junctions.forEach((x: HighLightableInterface):void => {

      let basic: HighLightInterface|null = this.templates.get('white') ?? null;

      if (!basic) {

        /* missing template? */

        return ;
      }

      x.highLight(basic);
    });

    /* the connections */

    this.connections.forEach((x: HighLightableInterface):void => {

      let basic: HighLightInterface|null = this.templates.get('default-connector-color') ?? null;

      if (!basic) {

        /* missing template? */

        return ;
      }

      x.highLight(basic);
    });

  }

  /** apply global highlighting for this connection being selected */
  public highlightConnection(connection: Connection): void {

    let hm: HighLightManager = this;

    Logger.debug(`HighLightManager highlighting connection: `, connection);

    let me: Connection = connection;

    if (!me.src || !me.dst) {

      /* missing ports? */

      return ;
    }

    /*
     * highlight rules:
     *
     *   0. Set all label colors to Green, and connection colors to Green  (suggest color Chartreuse #7FFF00)
     *      default for everything is not blocked
     *
     *   1. identify “me” connection,
     *      - set begin/end label colors to red (Not blue as currently is done).  Suggest color “OrangeRed” #FF4500
     *      - set connection color to red suggest color “orangered” #ff4500
     *
     *   2. Loop through all connections (for con in all connections)
     *      - If me.parallel.filter((x: number)  => con.parallel.includes(x)); contains ANY links (intersection
     *        of sets is not zero)
     *        - Add con to the list of blocked connections
     *
     *    3. For each connection in blocked connections
     *       - set begin/end label colors to red, but different from before use FireBrick #B22222
     *       - set connection color to red, but different from the highlighted red use FireBrick #B22222
     *
     */

    let styles: Map<HighLightableInterface, HighLightInterface>      = new Map<TrackLabel, HighLightInterface>();

    /* first set all labels/connections to green; by default nothing is blocked */

    [...hm.east, ...hm.west, ...hm.junctions].forEach((x: HighLightableInterface): void => {

      styles.set(x, {
        id:    uuidv4(),
        color: '#7FFF00',
        opacity: 1.0
      });

    });

    hm.connections.forEach((x: HighLightableInterface): void => {

      styles.set(x, {
        id:    uuidv4(),
        color: '#7FFF00',
        opacity: 1.0
      });

    });

    /* override begin/end labels with red color */

    let srcLabel: TrackLabel|null          = hm.labelForTrack(me.src);
    let dstLabel: TrackLabel|null          = hm.labelForTrack(me.dst);

    if (srcLabel && dstLabel) {

      [srcLabel, dstLabel, me].forEach((x: HighLightableInterface): void => {

        styles.set(x, {
          id: uuidv4(),
          color: '#FF4500',
          opacity: 1.0
        });

      });
    }

    /* loop through all the connections, looking for any that are blocked by 'me' ... */

    hm.connections.forEach((x: HighLightableInterface): void => {

      let other: Connection = x as Connection;

      if (x == me) {

        /* we've already handled 'me' */

        return ;
      }

      let intersection: number[] = me.parallel.filter((x: number) => other.parallel.includes(x));

      if (intersection.length == 0) {

        /* no intersection between 'me' and this other connection */

        return ;
      }

      /* the 'other' is blocked by 'me'; they have overlapping 'parallel' link sets. */

      /* because 'other' is blocked, we style it and its src/dst labels red... */

      let srcLabel: TrackLabel|null          = hm.labelForTrack(other.src);
      let dstLabel: TrackLabel|null          = hm.labelForTrack(other.dst);

      if (srcLabel && dstLabel) {

        [srcLabel, dstLabel, other].forEach((x: HighLightableInterface): void => {

          styles.set(x, {
            id: uuidv4(),
            color: '#B22222',
            opacity: 1.0
          });

        });
      }

    });

    /* at this point all the styles have been found, we just need to apply them */

    for ( let [item, style] of styles.entries()) {

      hm.highLight(item, style);
    }

  }

}
